import {
  useQuery,
  useQueryClient,
  useMutation,
  keepPreviousData,
} from '@tanstack/vue-query'
import type { PaginationState, SortingState } from '@tanstack/vue-table'
import type { Schemas } from '@/api/types.js'
import {
  API_CLIENTS_LIST_URL,
  API_CLIENTS_DETAIL_URL,
  API_CLIENTS_BALANCE_URL,
  API_CLIENTS_CHECK_DUPLICATE_URL,
  ClientGenderOptions,
  type ClientListQueryParams,
  type ClientCreateBody,
  type ClientMatchResponse,
  type ClientMatchQueryParams,
  type CreateClient,
  type NormalizedClientMatchData,
  type NormalizedClient,
  type NormalizedClientListResponse,
  type PhoneApiPayload,
  type ClientGender,
  type HoldingPlaceNumberApiPayload,
  type ClientCreateData,
} from '@/models/client.js'
import { useApi } from '@/composables/useApi.js'
import useTranslation from '@/composables/useTranslation.js'
import {
  buildIncludedResourceMap,
  createRelationshipBuilder,
  many,
  one,
  toPaginationQueryParams,
  type IncludedResourceMap,
  type RelationshipDefs,
} from '@/utils/jsonapi.js'
import { sortingStateToQueryParam } from '@/utils/sorting.js'
type ClientIncludedResourceMap = IncludedResourceMap<'Patient' | 'Phone'>

const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: (query: MaybeRef<ClientListQueryParams>) =>
    [...clientKeys.lists(), query] as const,
  detail: (id: MaybeRef<string>) => [...clientKeys.all, 'detail', id] as const,
  balance: (id: MaybeRef<string | undefined>) =>
    [...clientKeys.all, 'balance', id] as const,
}

function normalizeClientMatchData({ data, included }: ClientMatchResponse) {
  return data.map(
    (match) =>
      ({
        id: match.id,
        firstName: match.attributes?.first_name,
        lastName: match.attributes?.last_name,
        idNumber: match.attributes?.id_number,
        email: match.attributes?.email,
        phone:
          (included?.find((el) => el.attributes?.preferred_reminders)
            ?.attributes?.phone_number as string) || '',
        streetAddress: match.attributes?.street_address,
        streetAddress2: match.attributes?.street_address_2 || '',
        streetAddress3: match.attributes?.street_address_3 || '',
        zipCode: match.attributes?.zip_code,
        city: match.attributes?.city,
        customerType: match.attributes?.customer_type,
        registerNumber: match.attributes?.register_number,
      }) satisfies NormalizedClientMatchData,
  )
}

/**
 * Normalize a client response to a more usable format.
 * @param client - The client response to normalize.
 * @param included - The included resource map.
 * @returns The normalized client.
 */
function normalizeClient(
  client: Schemas['Client'],
  included?: ClientIncludedResourceMap,
): NormalizedClient {
  const normalizedItem: NormalizedClient = {
    ...client,
  }

  if (included) {
    const relationships = buildClientRelationships(client, included)
    normalizedItem.phoneNumbers = relationships.phone_numbers
    normalizedItem.patients = relationships.patients
  }

  return normalizedItem
}

export function useClientDetailQuery(
  clientId: MaybeRef<string>,
  includes: MaybeRef<string[]> = [],
  enabled: MaybeRef<boolean> = true,
) {
  const api = useApi()
  const route = useRoute()

  const query = useQuery({
    queryKey: clientKeys.detail(clientId),
    enabled,
    queryFn: async () =>
      await api((client) =>
        client.GET(API_CLIENTS_DETAIL_URL, {
          params: {
            path: {
              provet_id: route.params.provetId,
              id: unref(clientId),
            },
            query: {
              include: unref(includes),
            },
          },
        }),
      ),
  })

  return reactive(query)
}

export function useClientBalanceQuery({
  clientId,
  enabled = true,
}: {
  clientId: MaybeRef<string | undefined>
  enabled?: MaybeRef<boolean>
}) {
  const api = useApi()
  const route = useRoute()

  const query = useQuery({
    queryKey: clientKeys.balance(clientId),
    enabled: computed(() => unref(enabled) && Boolean(unref(clientId))),
    queryFn: () =>
      api((client) =>
        client.GET(API_CLIENTS_BALANCE_URL, {
          params: {
            path: {
              provet_id: route.params.provetId,
              id: unref(clientId)!,
            },
          },
        }),
      ),
  })

  return reactive(query)
}

interface ClientListFilters {
  query?: MaybeRefOrGetter<string>
  referringOrganization?: MaybeRefOrGetter<boolean>
}

export function useClientListQuery({
  pagination,
  sorting,
  filters = {},
  includes = [],
}: {
  pagination: MaybeRefOrGetter<PaginationState>
  sorting: MaybeRefOrGetter<SortingState>
  filters?: ClientListFilters
  includes?: MaybeRefOrGetter<ClientListQueryParams['include']>
}) {
  const api = useApi()
  const route = useRoute()

  const queries = computed<ClientListQueryParams>(() => ({
    ...toPaginationQueryParams(toValue(pagination)),
    'filter[query]': toValue(filters.query) || undefined,
    'filter[referring_organization]':
      toValue(filters.referringOrganization) || undefined,
    sort: sortingStateToQueryParam(toValue(sorting)),
    include: toValue(includes),
  }))

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryKey: clientKeys.list(queries),
    queryFn: async () =>
      await api((client) =>
        client.GET(API_CLIENTS_LIST_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: queries.value,
          },
        }),
      ),
    select: (response): NormalizedClientListResponse => {
      const included = buildIncludedResourceMap(response, ['Patient', 'Phone'])

      return {
        ...response,
        data: response.data.map((client) => normalizeClient(client, included)),
      }
    },
  })

  return reactive(query)
}

export function useClientQueryData(
  clientId: MaybeRef<string>,
  queryInclusion: string[],
  enabled: MaybeRef<boolean> = true,
) {
  const clientQuery = useClientDetailQuery(clientId, queryInclusion, enabled)

  const clientData = computed(() => {
    const data = clientQuery?.data?.data?.attributes

    const rawPatients =
      data && queryInclusion.includes('patients')
        ? Object.values(
            buildIncludedResourceMap(clientQuery.data ?? {}, ['Patient'])
              ?.Patient || {},
          )
        : []

    const patients = rawPatients.map((patient) => ({
      label: patient.attributes?.name || '',
      value: patient.id,
    }))

    const phones =
      data && queryInclusion.includes('phone_numbers')
        ? Object.values(
            buildIncludedResourceMap(clientQuery.data ?? {}, ['Phone'])
              ?.Phone || {},
          )
        : []

    const emails = data?.email
      ? [data.email, ...(data?.alt_emails?.split(',') || [])]
          .filter(Boolean)
          .map((email) => email?.trim())
          .filter((email): email is string => email !== '')
      : []

    return {
      id: clientQuery?.data?.data?.id,
      fullName: data?.full_name,
      phones,
      emails,
      noEmail: data?.no_email,
      patients,
    }
  })

  return {
    clientData,
    clientQuery,
  }
}

export function useClientCreateMutation() {
  const queryClient = useQueryClient()
  const api = useApi()
  const route = useRoute()

  return useMutation({
    mutationFn: ({
      attributes,
      relationships,
    }: {
      attributes: CreateClient
      relationships?: {
        home_department?: string
        invoicing_client?: string
        parent_referring_organization?: string
        phone_numbers?: PhoneApiPayload[]
        tags?: string[]
        write_only_holding_place_numbers?: HoldingPlaceNumberApiPayload[]
      }
    }) => {
      const data: ClientCreateData = {
        type: 'Client',
        attributes,
      }

      if (relationships) {
        data.relationships = {
          home_department: relationships.home_department
            ? {
                data: {
                  type: 'Department',
                  id: relationships.home_department,
                },
              }
            : undefined,
          invoicing_client: relationships.invoicing_client
            ? {
                data: {
                  type: 'Client',
                  id: relationships.invoicing_client,
                },
              }
            : undefined,
          parent_referring_organization:
            relationships.parent_referring_organization
              ? {
                  data: {
                    type: 'Client',
                    id: relationships.parent_referring_organization,
                  },
                }
              : undefined,
          write_only_tag_texts: relationships.tags
            ? {
                data: relationships.tags.map((id) => ({
                  id,
                  type: 'TagText',
                })),
              }
            : undefined,
          write_only_phone_numbers: relationships.phone_numbers?.length
            ? {
                data: relationships.phone_numbers,
              }
            : undefined,
          write_only_holding_place_numbers: relationships
            .write_only_holding_place_numbers?.length
            ? {
                data: relationships.write_only_holding_place_numbers,
              }
            : undefined,
        }
      }

      return api((client) =>
        client.POST(API_CLIENTS_LIST_URL, {
          params: {
            path: { provet_id: route.params.provetId },
          },
          body: { data: data as ClientCreateBody['data'] },
        }),
      )
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() }),
  })
}

export function useClientMatchQuery({
  enabled = false,
  pagination = { pageIndex: 0, pageSize: 5 },
  filters,
}: {
  enabled?: MaybeRef<boolean>
  pagination?: MaybeRef<PaginationState>
  filters: MaybeRef<ClientMatchQueryParams>
}) {
  const api = useApi()
  const route = useRoute()

  const queries = computed<ClientMatchQueryParams>(() => {
    const unrefFilters = unref(filters) || {}

    return {
      ...toPaginationQueryParams(pagination),
      include: ['phone_numbers'],
      email: unrefFilters.email,
      firstname: unrefFilters.firstname,
      id_number: unrefFilters.id_number,
      lastname: unrefFilters.lastname,
      street_address: unrefFilters.street_address,
      zip_code: unrefFilters.zip_code,
      register_number: unrefFilters.register_number,
    }
  })

  // Enable prefetching during rendering via `experimental_prefetchInRender` option.
  // It's used to prefetch the data when using the `clientMatchQuery` composable.
  const query = useQuery({
    enabled,
    experimental_prefetchInRender: true,
    queryKey: clientKeys.list(queries),
    queryFn: async () => {
      return api((client) =>
        client.GET(API_CLIENTS_CHECK_DUPLICATE_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: queries.value,
          },
        }),
      )
    },
    select: (response) => ({
      ...response,
      data: normalizeClientMatchData(response),
    }),
  })

  return reactive(query)
}

const clientRelationships = {
  tag_texts: many('TagText'),
  write_only_tag_texts: many('TagText'),
  phone_numbers: many('Phone'),
  write_only_phone_numbers: many('Phone'),
  merged_into: one('Client'),
  home_department: one('Department'),
  created_user: one('User'),
  modified_user: one('User'),
  parent_referring_organization: one('Client'),
  invoicing_client: one('Client'),
  alternative_payer: one('AlternativePayer'),
  patients: many('Patient'),
} satisfies RelationshipDefs<Schemas['Client']>

export const buildClientRelationships =
  createRelationshipBuilder(clientRelationships)

export function useClientGender() {
  const tEnumGender = useTranslation('common.enums.clientGender')

  const translate = (gender: MaybeRefOrGetter<ClientGender>) => {
    const key = toValue(gender)

    return {
      [ClientGenderOptions.Male]: tEnumGender(ClientGenderOptions.Male),
      [ClientGenderOptions.Female]: tEnumGender(ClientGenderOptions.Female),
      [ClientGenderOptions.Other]: tEnumGender(ClientGenderOptions.Other),
    }[key]
  }

  const options = [
    {
      label: tEnumGender(ClientGenderOptions.Male),
      value: ClientGenderOptions.Male,
    },
    {
      label: tEnumGender(ClientGenderOptions.Female),
      value: ClientGenderOptions.Female,
    },
    {
      label: tEnumGender(ClientGenderOptions.Other),
      value: ClientGenderOptions.Other,
    },
  ]

  return {
    options,
    translate,
  }
}
